<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useAuthStore } from '@/stores/auth.store'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import { isRouteVisible } from '@/utils/router-helper.ts'
import Menubar from 'primevue/menubar'

const route = useRoute()
const { t } = useTranslation()
const authStore = useAuthStore()
const { user, isOrganizer, isReviewer } = storeToRefs(authStore)

type NavItem = {
    label: string
    to: { name: string }
    icon?: string
}

const navItems = computed(() => [
    {
        label: t('ui.organizer.formats.overview'),
        to: { name: 'formats' },
        align: 'left',
    },
    {
        label: t('ui.navigation.applications'),
        to: { name: 'participant-applications' },
        align: 'left',
    },
    {
        label: t('ui.navigation.participations'),
        to: { name: 'participant-participations' },
        align: 'left',
    },
    // NOTE: The watchlist for organizers is not part of the MVP. It will follow up in a later version
    ...(!isOrganizer.value && !isReviewer.value
        ? [
              {
                  label: t('ui.navigation.watchlist'),
                  to: { name: 'watchlist' },
                  icon: 'pi pi-bookmark',
                  align: 'right',
              },
          ]
        : []),
    {
        label: t('ui.navigation.profile'),
        to: { name: 'profile' },
        icon: 'pi pi-user',
        align: 'right',
    },
])

const leftItems = computed<NavItem[]>(() => {
    if (!user.value) return []

    return navItems.value.filter((navItem) => {
        const isReviewerFormatRoute = navItem.to.name === 'formats' && isReviewer.value

        return navItem.align == 'left' && (isRouteVisible(navItem.to) || isReviewerFormatRoute)
    })
})

const rightItems = computed<NavItem[]>(() => {
    if (!user.value) return []

    return navItems.value.filter((navItem) => {
        return navItem.align == 'right' && isRouteVisible(navItem.to)
    })
})

const isActive = (routeName: string) => route.name === routeName
</script>

<template>
    <Menubar
        v-if="user"
        :pt="{ root: 'border-0 border-b pt-0 md:px-10 md:pb-4 gap-12' }"
        :aria-label="t('ui.navigation.subNavigation')"
    >
        <template #start>
            <Button
                v-for="item in leftItems"
                :key="item.to.name"
                :to="item.to"
                as="router-link"
                :class="isActive(item.to.name) ? 'bg-secondary text-secondary' : ''"
                :data-testid="`sub-nav-link-${item.to.name}`"
                :icon="item.icon || ''"
                :label="item.label"
                size="small"
                text
                severity="contrast"
            />
        </template>
        <template #end>
            <div class="flex gap-2">
                <Button
                    v-for="item in rightItems"
                    :key="item.to.name"
                    :to="item.to"
                    as="router-link"
                    :class="isActive(item.to.name) ? 'bg-secondary text-secondary' : ''"
                    :data-testid="`sub-nav-link-${item.to.name}`"
                    :icon="item.icon || ''"
                    :label="item.label"
                    size="small"
                    text
                    severity="contrast"
                    :pt="{ label: 'md:inline hidden' }"
                />
            </div>
        </template>
    </Menubar>
</template>
